/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import React, { Fragment, useEffect, useState } from "react";
import { SimpliCityAdminSDK } from "@simplicitycms/simplicity-admin-js-sdk";
import { useAuth } from "cms/auth/hooks/use-auth";
import { LinkContext } from "cms/links/link-selector/context";
import { LinkSelectDialog } from "cms/links/link-selector/dialog-select";
import { LinkEditDialog } from "cms/links/link-selector/dialog-edit";
import { LinkCreateDialog } from "cms/links/link-selector/dialog-create";
import { LinkDisplay } from "cms/links/link-selector/display";
import { DefaultAddLinkButton } from "cms/links/link-selector/exported/add-button";
import { DefaultDisplayComponent } from "cms/links/link-selector/exported/display-component";
import { MODES, startingMode, EDIT_TABS, EDIT_TABS_INDEX, startingEditTab } from "cms/links/link-selector/helpers";
import { objectHasPropertyString } from "utils/empty-exists";
import { Logger } from "cms/utils/log/logger";

const LinkSelector = (props) => {

    const AddComponent = props?.AddComponent;
    const DisplayComponent = props?.DisplayComponent;
    const showSave = props?.showSave;
    const onSelectLinkHandler = props?.onSelectLink;
    const onLinkRemoved = props?.onLinkRemoved;
    const onLinkDeleted = props?.onLinkDeleted;
    const link = props?.link;
    const setLink = props?.setLink;
    const disableDisplay = props?.disableDisplay;
    const handleCancel = props?.handleCancel;

    const { token } = useAuth();
    const defaultLinkInitState = { link: { text: "", url: "", target: "" } };

    const [selectedLink, setSelectedLink] = useState();
    const [links, setLinks] = useState([]);
    const [needsRefresh, setNeedsRefresh] = useState(false);
    const [original, setOriginal] = useState();
    const [open, setOpen] = useState(false);
    const [openEdit, setOpenEdit] = useState(false);
    const [openRemove, setOpenRemove] = useState(false);
    const [openCreate, setOpenCreate] = useState(false);
    const [hasLink, setHasLink] = useState(false);
    const [startClone, setStartClone] = useState(false);
    const [localState, setLocalState] = useState(defaultLinkInitState);
    const [mode, setMode] = useState(startingMode);
    const [selectedTab, setSelectedTab] = useState(startingEditTab);

    useEffect(() => {
        if (!link || !objectHasPropertyString(link, "guid")) {
            setMode(MODES.SELECT);
            setHasLink(false);
        } else {
            setOriginal(link);
            setSelectedLink(link);
            setHasLink(true);
        }
    }, [link]);

    useEffect(() => {
        if (selectedLink) {
            setHasLink(true);
        }
    }, [selectedLink]);

    useEffect(() => {
        if (selectedLink) {
            setHasLink(true);
        }
    }, [selectedLink]);

    const handleTabsChange = (event, newValue) => {
        setSelectedTab(newValue);
        switch (newValue) {
            case 1:
                setSelectedTab(newValue);
                break;
            case 2:
                setSelectedTab(newValue);
                break;
            default:
                break;
        }
    };
    const onClickChange = async () => {
        setOpen(true);
    };
    const onClickEdit = async () => {
        setOpenEdit(true);
    };
    const onOpenCreate = async () => {
        setOpenCreate(true);
    };
    const onClone = () => {
        setStartClone(true);
        setOpenCreate(true);
    };
    const onSelectLink = (selectLink) => {
        Logger.debug("onSelectLink", selectLink);
        setSelectedLink(selectLink);
        setLink(selectLink);
        onSelectLinkHandler(selectLink);
        doClose();
    };
    const onRemove = async () => {
        setLink(undefined);
        setSelectedLink(undefined);
        onLinkRemoved();
    };
    const onDelete = async (link) => {
        if (link?.guid) {
            if (original?.guid === link?.guid) {
                setLink(undefined);
                setSelectedLink(undefined);
                await onLinkRemoved();
            }
            const guid = link?.guid;
            const link_response = await SimpliCityAdminSDK.link.delete(token, guid);
            Logger.debug("Delete Link",link_response);
            setNeedsRefresh(true);
        }
    };
    const handleClose = () => {
        doClose();
        if (typeof handleCancel === "function") {
            handleCancel();
        }
    };
    const doClose = () => {
        setSelectedLink(original);
        setMode(MODES.SELECT);
        setOpen(false);
    };
    const handleEditClose = () => {
        setOpenEdit(false);
        setSelectedTab(EDIT_TABS_INDEX.PROPERTIES);
    };
    const handleCreateClose = () => {
        setOpenCreate(false);
    };

    const linkCTX = {
        open,
        setOpen,
        openEdit,
        openCreate,
        openRemove,
        setOpenEdit,
        setOpenRemove,
        handleClose,
        handleEditClose,
        link,
        setLink,
        hasLink,
        onChange: onClickChange,
        onEdit: onClickEdit,
        onRemove,
        onDelete,
        onOpenCreate,
        onClone,
        // onSaveLink,
        onSelectLink,
        onLinkDeleted,
        onLinkRemoved,
        handleCreateClose,
        selectedLink,
        setSelectedLink,
        MODES,
        mode,
        setMode,
        startingMode,
        selectedTab,
        setSelectedTab,
        startingEditTab,
        EDIT_TABS,
        handleTabsChange,
        showSave,
        links,
        setLinks,
        localState,
        setLocalState,
        needsRefresh,
        setNeedsRefresh,
        startClone,
        setStartClone,
    };

    return (
        <Fragment>
            <LinkContext.Provider value={linkCTX}>

                {/* Display the Link Display Card - also used to launch the selector again  */}
                {!disableDisplay &&
                    <LinkDisplay
                        AddComponent={AddComponent}
                        DisplayComponent={DisplayComponent}
                    />
                }

                {/*  Display the link selector list dialog  */}
                <LinkSelectDialog />

                {/*  Display the link editing dialog  */}
                <LinkEditDialog />

                {/*  Display the link create dialog  */}
                <LinkCreateDialog />

            </LinkContext.Provider>
        </Fragment>
    );
};

export {
    DefaultDisplayComponent,
    DefaultAddLinkButton,
    LinkSelectDialog,
    LinkSelector
};
